<template>
    <section class="bg-white px-5 py-16 dark:bg-surface-card lg:px-20">
        <div class="container mx-auto">
            <div class="mb-8 flex items-center justify-between">
                <div>
                    <h2 class="font-semibold text-3xl text-gray-900 dark:text-white mb-2">
                        Najnovšie z blogu
                    </h2>
                    <p class="text-base text-gray-600 dark:text-gray-400">
                        Inšpirácia, tipy a príbehy pre tvoj rast
                    </p>
                </div>
                <NuxtLink
                    to="/blog"
                    class="hidden md:flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold transition-colors"
                >
                    <span>Všetky články</span>
                    <span>→</span>
                </NuxtLink>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="overflow-x-auto scrollbar-hide lg:overflow-visible">
                <div :class="cardRowClass">
                    <div v-for="i in 4" :key="i" class="flex-shrink-0 w-[320px] bg-gray-100 dark:bg-surface-card rounded-2xl overflow-hidden animate-pulse">
                    <div class="h-[160px] bg-gray-200 dark:bg-surface-card"></div>
                    <div class="p-5">
                        <div class="h-4 bg-gray-200 dark:bg-surface-card rounded w-1/3 mb-3"></div>
                        <div class="h-5 bg-gray-200 dark:bg-surface-card rounded w-full mb-2"></div>
                        <div class="h-5 bg-gray-200 dark:bg-surface-card rounded w-2/3 mb-4"></div>
                        <div class="h-4 bg-gray-200 dark:bg-surface-card rounded w-full mb-4"></div>
                        <div class="flex items-center gap-3">
                            <div class="size-8 bg-gray-200 dark:bg-surface-card rounded-full"></div>
                            <div class="flex-1">
                                <div class="h-3 bg-gray-200 dark:bg-surface-card rounded w-20 mb-1"></div>
                                <div class="h-3 bg-gray-200 dark:bg-surface-card rounded w-16"></div>
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
            </div>

            <!-- Blog Cards -->
            <div v-else-if="posts.length > 0" class="overflow-x-auto scrollbar-hide lg:overflow-visible">
                <div :class="cardRowClass">
                    <HomepageBlogCard
                        v-for="post in posts"
                        :key="post.id"
                        :post="post"
                    />
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-12 bg-gray-50 dark:bg-surface-card rounded-2xl">
                <div class="text-4xl mb-4">📝</div>
                <p class="text-gray-600 dark:text-gray-400">Zatiaľ tu nie sú žiadne články</p>
            </div>

            <!-- Mobile CTA -->
            <div class="md:hidden mt-6 text-center">
                <NuxtLink
                    to="/blog"
                    class="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-semibold"
                >
                    <span>Všetky články</span>
                    <span>→</span>
                </NuxtLink>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { BlogPost } from '#shared/types/Blog'
import { useBlogPost } from '@/composables/blog/useBlogPost'
import HomepageBlogCard from './HomepageBlogCard.vue'

const { loadLatestPosts } = useBlogPost()

// Scroll row on mobile; from lg a grid of the first 3 (xl: 4) cards, like the other homepage rows
const cardRowClass =
    'flex gap-6 pb-4 lg:grid lg:grid-cols-3 xl:grid-cols-4 lg:[&>*]:w-auto lg:[&>*:nth-child(n+5)]:hidden lg:max-xl:[&>*:nth-child(4)]:hidden'

// Stable key dedupes across remounts when pages/index.vue swaps Generic ↔ Personalized
// after auth resolves — without this, the blog posts get fetched twice for logged-in users.
// Fetched on the server so the (SWR-cached) homepage HTML links real articles instead of an empty state.
const { data: posts, status } = await useAsyncData<BlogPost[]>(
    'homepage-latest-blog-posts-5',
    () => loadLatestPosts(5),
    { default: () => [], lazy: true }
)

// Skeleton until the request has finished; the empty state is only for a finished, empty result
const loading = computed(() => status.value === 'idle' || status.value === 'pending')
</script>

<style scoped>
/* Hide scrollbar but keep functionality */
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
</style>
